import { useState } from 'react';
import { Plus, Timer, Trash2, Link2, X } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getStatusConfig, getPriorityConfig, formatDate } from '../../utils/helpers';
import Badge from '../common/Badge';
import Avatar from '../common/Avatar';
import SprintCard from './SprintCard';
import ConfirmDialog from '../common/ConfirmDialog';
import * as api from '../../services/api';

export default function SprintList() {
  const { state, dispatch, getUserById, getTasksForSprint, apiCreateSprint, apiDeleteSprint } = useApp();
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(
    state.sprints.find((s) => s.status === 'active')?.id || state.sprints[0]?.id || null
  );
  const [showNewForm, setShowNewForm] = useState(false);
  const [deleteSprintId, setDeleteSprintId] = useState<string | null>(null);
  const [showTaskPicker, setShowTaskPicker] = useState(false);
  const [assigning, setAssigning] = useState<string | null>(null);

  const selectedSprint = state.sprints.find((s) => s.id === selectedSprintId);
  const sprintTasks = selectedSprintId ? getTasksForSprint(selectedSprintId) : [];

  // Tasks in the current board that are NOT in any sprint
  const availableTasks = state.tasks.filter(
    (t) => t.boardId === state.currentBoardId && !t.sprintId
  );

  const handleCreateSprint = async (name: string, goal: string) => {
    try {
      await apiCreateSprint({
        name,
        goal,
        boardId: state.currentBoardId,
        status: 'planning',
        startDate: new Date().toISOString(),
        endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        taskIds: [],
      });
      setShowNewForm(false);
    } catch (error) {
      console.error('Failed to create sprint:', error);
    }
  };

  const handleDeleteSprint = async () => {
    if (deleteSprintId) {
      await apiDeleteSprint(deleteSprintId);
      if (selectedSprintId === deleteSprintId) {
        setSelectedSprintId(state.sprints.find((s) => s.id !== deleteSprintId)?.id || null);
      }
      setDeleteSprintId(null);
    }
  };

  const handleAssignTask = async (taskId: string) => {
    if (!selectedSprintId || assigning === taskId) return;
    setAssigning(taskId);
    try {
      await api.assignTaskToSprint(taskId, selectedSprintId);
      // Update local state
      dispatch({
        type: 'UPDATE_TASK',
        payload: { ...state.tasks.find((t) => t.id === taskId)!, sprintId: selectedSprintId },
      });
    } finally {
      setAssigning(null);
    }
  };

  const handleUnassignTask = async (taskId: string) => {
    if (assigning === taskId) return;
    setAssigning(taskId);
    try {
      await api.assignTaskToSprint(taskId, null);
      dispatch({
        type: 'UPDATE_TASK',
        payload: { ...state.tasks.find((t) => t.id === taskId)!, sprintId: null },
      });
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-80 xl:w-96 shrink-0 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-slate-700">Todos os Sprints</h2>
            <button
              onClick={() => setShowNewForm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 transition-colors"
            >
              <Plus size={14} />
              Novo Sprint
            </button>
          </div>

          {showNewForm && (
            <NewSprintInline
              onSubmit={handleCreateSprint}
              onCancel={() => setShowNewForm(false)}
            />
          )}

          {state.sprints.map((sprint) => (
            <SprintCard
              key={sprint.id}
              sprint={sprint}
              onSelect={setSelectedSprintId}
              isSelected={selectedSprintId === sprint.id}
            />
          ))}
        </div>

        <div className="flex-1 min-w-0">
          {selectedSprint ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-4 md:px-6 py-4 border-b border-slate-100">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Timer size={18} className="text-brand-500" />
                    <h3 className="text-lg font-semibold text-slate-800">
                      {selectedSprint.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowTaskPicker(!showTaskPicker)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 transition-colors"
                      title="Adicionar tarefas ao sprint"
                    >
                      <Link2 size={14} />
                      Adicionar Tarefas
                    </button>
                    <button
                      onClick={() => setDeleteSprintId(selectedSprint.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-slate-500">{selectedSprint.goal}</p>
              </div>

              {/* Task picker panel */}
              {showTaskPicker && (
                <div className="border-b border-slate-100 bg-slate-50 px-4 md:px-6 py-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-slate-700">
                      Tarefas disponíveis do quadro
                    </h4>
                    <button
                      onClick={() => setShowTaskPicker(false)}
                      className="p-1 rounded text-slate-400 hover:text-slate-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {availableTasks.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">
                      Todas as tarefas do quadro já estão em um sprint
                    </p>
                  ) : (
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {availableTasks.map((task) => {
                        const statusCfg = getStatusConfig(task.status);
                        const isLoading = assigning === task.id;
                        return (
                          <div
                            key={task.id}
                            className="flex items-center gap-3 px-3 py-2 bg-white rounded-lg border border-slate-200"
                          >
                            <span className="flex-1 text-sm text-slate-700 truncate">{task.title}</span>
                            <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                            <button
                              onClick={() => handleAssignTask(task.id)}
                              disabled={isLoading}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 transition-colors shrink-0"
                            >
                              {isLoading ? '...' : <><Plus size={12} />Adicionar</>}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Tarefa
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Status
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Prioridade
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Responsável
                      </th>
                      <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Pontos
                      </th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">
                        Prazo
                      </th>
                      <th className="py-3 px-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {sprintTasks.map((task) => {
                      const statusCfg = getStatusConfig(task.status);
                      const priorityCfg = getPriorityConfig(task.priority);
                      const assignee = task.assigneeIds[0]
                        ? getUserById(task.assigneeIds[0])
                        : null;
                      const isLoading = assigning === task.id;

                      return (
                        <tr
                          key={task.id}
                          onClick={() =>
                            dispatch({ type: 'SELECT_TASK', payload: task.id })
                          }
                          className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors"
                        >
                          <td className="py-3 px-4">
                            <span className="text-sm font-medium text-slate-700">
                              {task.title}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={priorityCfg.color} dot={priorityCfg.dot}>
                              {priorityCfg.label}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            {assignee && (
                              <div className="flex items-center gap-2">
                                <Avatar
                                  name={assignee.name}
                                  color={assignee.avatarColor}
                                  size="sm"
                                />
                                <span className="text-sm text-slate-600 hidden md:inline">
                                  {assignee.name}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-sm text-slate-600">{task.storyPoints}</span>
                          </td>
                          <td className="py-3 px-4">
                            {task.dueDate && (
                              <span className="text-sm text-slate-500">
                                {formatDate(task.dueDate)}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => handleUnassignTask(task.id)}
                              disabled={isLoading}
                              className="p-1 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                              title="Remover do sprint"
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {sprintTasks.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="text-center py-12 text-sm text-slate-400"
                        >
                          Nenhuma tarefa neste sprint.{' '}
                          <button
                            className="text-brand-500 hover:underline"
                            onClick={() => setShowTaskPicker(true)}
                          >
                            Adicionar tarefas
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-white rounded-2xl border border-slate-200">
              <p className="text-sm text-slate-400">Selecione um sprint para ver detalhes</p>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!deleteSprintId}
        title="Excluir Sprint"
        message="Tem certeza que deseja excluir este sprint? As tarefas associadas serão desvinculadas."
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={handleDeleteSprint}
        onCancel={() => setDeleteSprintId(null)}
      />
    </div>
  );
}

function NewSprintInline({
  onSubmit,
  onCancel,
}: {
  onSubmit: (name: string, goal: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');

  return (
    <div className="p-4 bg-white rounded-2xl border border-brand-200 shadow-sm animate-scale-in">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">Novo Sprint</h4>
      <div className="space-y-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome do sprint..."
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
          autoFocus
        />
        <input
          type="text"
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          placeholder="Meta do sprint..."
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
        />
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={() => name.trim() && onSubmit(name.trim(), goal.trim())}
            disabled={!name.trim()}
            className="flex-1 px-3 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-40 transition-colors"
          >
            Criar
          </button>
        </div>
      </div>
    </div>
  );
}
